import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Flame, Target, BookHeart, MessageCircle, ChevronRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import MimoCharacter from '../components/MimoCharacter';
import EnergyBar from '../components/EnergyBar';
import Navigation from '../components/Navigation';
import { Card, CardContent } from '../components/ui/card';

export const Dashboard = () => {
  const navigate = useNavigate();
  const { user, stats, fetchStats, getAgeConfig } = useUser();
  const config = getAgeConfig();
  const [mimoMood, setMimoMood] = useState('happy');

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    // Update MIMO mood based on energy
    if (user?.energy_level >= 80) setMimoMood('excited');
    else if (user?.energy_level >= 50) setMimoMood('happy');
    else if (user?.energy_level >= 30) setMimoMood('calm');
    else setMimoMood('sad');
  }, [user?.energy_level]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return '¡Buenos días';
    if (hour < 18) return '¡Buenas tardes';
    return '¡Buenas noches';
  };

  const getMimoMessage = () => {
    const messages = {
      explorador: [
        '¡Hoy es un gran día para brillar! ✨',
        '¿Listo para ganar estrellas? 🌟',
        '¡Vamos a divertirnos juntos! 🎉'
      ],
      companero: [
        '¿Cómo te sientes hoy?',
        'Estoy aquí para lo que necesites.',
        '¡Juntos podemos con todo!'
      ],
      aliado: [
        '¿Listo para un día productivo?',
        'Cada pequeño paso cuenta.',
        'Tu bienestar es la prioridad.'
      ]
    };
    const ageMessages = messages[user?.age_mode] || messages.aliado;
    return ageMessages[Math.floor(Math.random() * ageMessages.length)];
  };

  const quickActions = [
    { 
      label: 'Hábitos', 
      icon: Target, 
      path: '/habits',
      color: '#FF7A59',
      stat: stats ? `${stats.completed_today}/${stats.total_habits}` : '0/0'
    },
    { 
      label: 'Diario', 
      icon: BookHeart, 
      path: '/diary',
      color: '#4F8CFF',
      stat: stats ? `${stats.diary_entries} entradas` : '0 entradas'
    },
    { 
      label: 'Conversar', 
      icon: MessageCircle, 
      path: '/chat',
      color: '#06D6A0',
      stat: 'Chat con MIMO'
    }
  ];

  return (
    <div className="min-h-screen bg-background pb-24 lg:pb-8 lg:pl-20">
      <Navigation />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className={`${config.fontClass} text-2xl sm:text-3xl font-bold text-foreground mb-1`}>
            {getGreeting()}, {user?.name?.split(' ')[0]}! 👋
          </h1>
          <p className="text-muted-foreground">{getMimoMessage()}</p>
        </motion.div>

        {/* MIMO Section */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <Card className={`glass ${config.cardRadius} overflow-hidden`}>
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row items-center gap-6">
                {/* MIMO */}
                <div className="relative">
                  <MimoCharacter 
                    mood={mimoMood} 
                    size="lg"
                    onClick={() => navigate('/chat')}
                  />
                </div>
                
                {/* Stats */}
                <div className="flex-1 space-y-4 w-full">
                  <EnergyBar />
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 rounded-xl bg-muted/50">
                      <div className="flex items-center gap-2 mb-1">
                        <Flame className="w-4 h-4 text-[#FF7A59]" />
                        <span className="text-sm text-muted-foreground">Racha</span>
                      </div>
                      <span className="text-xl font-bold text-foreground">
                        {user?.streak_days || 0} días
                      </span>
                    </div>
                    
                    <div className="p-3 rounded-xl bg-muted/50">
                      <div className="flex items-center gap-2 mb-1">
                        <Sparkles className="w-4 h-4 text-[#4F8CFF]" />
                        <span className="text-sm text-muted-foreground">Estado</span>
                      </div>
                      <span className="text-xl font-bold text-foreground capitalize">
                        {mimoMood === 'happy' ? 'Feliz' : 
                         mimoMood === 'excited' ? 'Radiante' :
                         mimoMood === 'calm' ? 'Tranquilo' : 'Cansado'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className={`${config.fontClass} text-lg font-semibold text-foreground mb-4`}>
            Acciones Rápidas
          </h2>
          
          <div className="grid gap-4">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <motion.button
                  key={action.path}
                  onClick={() => navigate(action.path)}
                  className={`w-full p-4 ${config.cardRadius} glass text-left card-hover`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  data-testid={`quick-action-${action.label.toLowerCase()}`}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center"
                      style={{ backgroundColor: `${action.color}15` }}
                    >
                      <Icon className="w-6 h-6" style={{ color: action.color }} />
                    </div>
                    <div className="flex-1">
                      <span className="font-semibold text-foreground">{action.label}</span>
                      <p className="text-sm text-muted-foreground">{action.stat}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </motion.button>
              );
            })}
          </div>
        </motion.div>

        {/* Today's Focus */}
        {stats && stats.total_habits > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-8"
          >
            <Card className={`glass ${config.cardRadius} border-[#FF7A59]/20`}>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Target className="w-5 h-5 text-[#FF7A59]" />
                  <h3 className="font-semibold text-foreground">Progreso de Hoy</h3>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-[#FF7A59] rounded-full"
                      initial={{ width: 0 }}
                      animate={{ 
                        width: `${(stats.completed_today / stats.total_habits) * 100}%` 
                      }}
                      transition={{ delay: 0.6, duration: 0.5 }}
                    />
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    {stats.completed_today}/{stats.total_habits}
                  </span>
                </div>
                
                {stats.completed_today === stats.total_habits && stats.total_habits > 0 && (
                  <p className="text-sm text-[#06D6A0] mt-3 font-medium">
                    🎉 ¡Completaste todos tus hábitos de hoy!
                  </p>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
